import React, { FC } from "react";

import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import SaveIcon from "@mui/icons-material/Save";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	InputAdornment,
	Typography,
} from "@mui/material";
import { Form, Formik } from "formik";

import { Translate } from "@/components/Application/Translate";
import ChecklistThemeListItems from "@/components/Procedures/NewProcedure/ChecklistThemeListItems";
import { ProcedureCompetencyListItem } from "@/components/Procedures/ProcedureCompetency/ProcedureCompetencyListItem";
import { ProcedureNodeFolderItem } from "@/components/Procedures/ProcedureNodeFolders/ProcedureNodeFolderItem";
import { ProcedureProjectOwnerListItem } from "@/components/Procedures/ProcedureProjectOwners/ProcedureProjectOwnerListItem";
import { EditProcedureValidation } from "@/components/Procedures/Validation/EditProcedureValidation";

import { AlertRequest } from "@/helpers/alerts/AlertRequest";
import { AlertToast } from "@/helpers/alerts/AlertToast";
import CustomCheckbox from "@/helpers/components/CustomFormFields/CustomCheckbox";
import CustomInput from "@/helpers/components/CustomFormFields/CustomInput";
import CustomSelect from "@/helpers/components/CustomFormFields/CustomSelect";
import CustomSelectCheckbox from "@/helpers/components/CustomFormFields/CustomSelectCheckbox";
import { Spinner } from "@/helpers/components/Spinner";

import { EditProcedure } from "@/model/Procedure/EditProcedure";
import { User } from "@/model/User/User";

import { useFetchGroups } from "@/service/GroupQueries";
import {
	useMutateDeleteProcedure,
	useMutateUpdateProcedureSettings,
} from "@/service/ProcedureMutations";
import {
	useFetchActiveProcedure,
	useFetchActiveProcedureGroupFolders,
	useFetchProcedureMetaData,
} from "@/service/ProcedureQueries";

interface EditProcedureFormProps {
	user: User | null;
	open: boolean;
	handleClose: () => void;
	id: number;
}

export const EditProcedureForm: FC<EditProcedureFormProps> = (props) => {
	const { handleClose, id, open } = props;

	const { data: procedureMetaData, isLoading } = useFetchProcedureMetaData("?");
	const { groupList } = useFetchActiveProcedureGroupFolders();
	const { data: currentProcedure } = useFetchActiveProcedure(id);
	const { updateProcedure } = useMutateUpdateProcedureSettings();
	const deleteProcedureAsync = useMutateDeleteProcedure();
	const { data: groups, isLoading: isLoadingGroups } = useFetchGroups();

	const handleDelete = async (values: EditProcedure) => {
		try {
			handleClose();
			AlertRequest.confirm(deleteProcedureAsync, values);
		} catch (error) {
			AlertRequest.failed();
			console.error(error);
		} finally {
			handleClose();
		}
	};

	const handleSubmit = async (values: EditProcedure) => {
		try {
			await updateProcedure(values);
			AlertToast.success();
		} catch (error) {
			AlertToast.failed();
			console.error(error);
		} finally {
			handleClose();
		}
	};

	if (
		isLoading ||
		isLoadingGroups ||
		!procedureMetaData ||
		!currentProcedure ||
		!groups
	) {
		return <Spinner text="Loading procedure data" />;
	}

	const isLink = (folderId: number) =>
		!!groupList.find((g) => g.Id === folderId && g.Link);

	const fieldSx = { "& .MuiInputBase-root": { height: 40, borderRadius: 2 } };
	const buttonSx = {
		height: 40,
		borderRadius: 2,
		textTransform: "none",
		fontWeight: 800,
	};

	return (
		<Dialog
			onClose={handleClose}
			open={open}
			fullWidth
			maxWidth="md"
			aria-labelledby="edit-procedure-title"
			PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
		>
			{/* Header */}
			<DialogTitle
				id="edit-procedure-title"
				sx={{ pr: 6, pb: 0.5, fontWeight: 800 }}
			>
				<Translate
					HTMLTag="span"
					className="heading"
					String="Procedures.EditProcedure.Header"
				>
					Edit procedure
				</Translate>
			</DialogTitle>

			{/* Close icon */}
			<IconButton
				aria-label="close"
				onClick={handleClose}
				sx={{ position: "absolute", right: 8, top: 8 }}
			>
				<CloseIcon />
			</IconButton>

			<DialogContent dividers sx={{ pt: 1.5 }}>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					<Translate String="Procedures.EditProcedure.Subtitle">
						Update metadata, ownership, and access settings for this procedure.
					</Translate>
				</Typography>

				<Formik
					initialValues={{
						ProcedureId: id,
						ProjectGroup: currentProcedure.GroupId,
						Name: currentProcedure.Name,
						Responsible: currentProcedure.Responsible.Id,
						TrainingCompetency: currentProcedure.TrainingType,
						ViewDesign: currentProcedure.ViewDesign,
						WriteProtected: currentProcedure.WriteProtected,
						ProcedureTags: currentProcedure.ProcedureTags,
						GroupsWithCompetencyRequirement:
							currentProcedure.GroupsWithCompetencyRequirement,
					}}
					validationSchema={EditProcedureValidation}
					onSubmit={handleSubmit}
					validateOnMount
				>
					{({ values, isValid, dirty }) => (
						<Form>
							<Grid container spacing={2}>
								{/* Name */}
								<Grid item xs={12}>
									<CustomInput
										type="text"
										label="Name"
										name="Name"
										id="procedureName"
										translationString="Procedures.NewProcedure.Name"
										fullWidth
										sx={fieldSx}
										placeholder="e.g. Maintenance routine for Pump A"
									/>
								</Grid>

								{/* Folder */}
								<Grid item xs={12} md={6}>
									<CustomSelect
										label="Folder"
										name="ProjectGroup"
										id="folder"
										translationString="Procedures.NewProcedure.Folder"
										fullWidth
										sx={fieldSx}
										InputProps={
											{
												startAdornment: (
													<InputAdornment position="start">
														<FolderOpenOutlinedIcon fontSize="small" />
													</InputAdornment>
												),
											} as any
										}
									>
										{procedureMetaData.Folders?.filter(
											(f) => f.Name !== "[Root]" && !isLink(f.GroupNumber),
										).map((folder) => (
											<ProcedureNodeFolderItem
												Folder={folder}
												key={folder.GroupNumber.toString()}
											/>
										))}
									</CustomSelect>
									<Typography variant="caption" color="text.secondary">
										<Translate String="Procedures.EditProcedure.Folder.Help">
											Choose where the procedure belongs.
										</Translate>
									</Typography>
								</Grid>

								{/* Owner */}
								<Grid item xs={12} md={6}>
									<CustomSelect
										label="Owner"
										name="Responsible"
										id="procedureLeader"
										translationString="Procedures.NewProcedure.Owner"
										fullWidth
										sx={fieldSx}
										InputProps={
											{
												startAdornment: (
													<InputAdornment position="start">
														<PersonOutlineOutlinedIcon fontSize="small" />
													</InputAdornment>
												),
											} as any
										}
									>
										<option value={0} />
										{procedureMetaData.ProjectOwners?.map((owner) => (
											<ProcedureProjectOwnerListItem
												Owner={owner}
												key={owner.Id}
											/>
										))}
									</CustomSelect>
								</Grid>

								{/* Competence */}
								<Grid item xs={12} md={6}>
									<CustomSelect
										label="Competence"
										name="TrainingCompetency"
										id="competence"
										translationString="Procedures.NewProcedure.Competency"
										fullWidth
										sx={fieldSx}
										InputProps={
											{
												startAdornment: (
													<InputAdornment position="start">
														<SchoolOutlinedIcon fontSize="small" />
													</InputAdornment>
												),
											} as any
										}
									>
										{procedureMetaData.Competency?.map((c) => (
											<ProcedureCompetencyListItem Competence={c} key={c.Id} />
										))}
									</CustomSelect>
								</Grid>

								{/* Checklist design */}
								<Grid item xs={12} md={6}>
									<CustomSelect
										label="Checklist design"
										name="ViewDesign"
										id="ViewDesign"
										translationString="Procedures.NewProcedure.ChecklistTemplate"
										fullWidth
										sx={fieldSx}
										InputProps={
											{
												startAdornment: (
													<InputAdornment position="start">
														<TuneOutlinedIcon fontSize="small" />
													</InputAdornment>
												),
											} as any
										}
									>
										<ChecklistThemeListItems />
									</CustomSelect>
								</Grid>

								{/* Tags */}
								<Grid item xs={12}>
									<CustomInput
										type="text"
										label="Procedure tags"
										name="ProcedureTags"
										id="ProcedureTags"
										translationString="Procedures.EditProcedure.ProcedureTags"
										fullWidth
										sx={fieldSx}
										placeholder="e.g. safety, critical, weekly"
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<SellOutlinedIcon fontSize="small" />
												</InputAdornment>
											),
										}}
									/>
									<Typography variant="caption" color="text.secondary">
										<Translate String="Procedures.EditProcedure.Tags.Help">
											Separate with commas to help users search.
										</Translate>
									</Typography>
								</Grid>

								{/* Groups with competence requirement */}
								<Grid item xs={12}>
									<CustomSelectCheckbox
										id="GroupsWithCompetencyRequirement"
										label="Groups with competence requirement"
										name="GroupsWithCompetencyRequirement"
										translationString="Training.EditProcedure.GroupsWithCompetenceRequirement"
										options={
											groups
												.filter((x) => x.GroupModules.Competence)
												.map((x) => ({ value: String(x.Id), label: x.Name })) ||
											[]
										}
										style={{ width: "100%" }}
										disabled={values.TrainingCompetency.toString() === "0"}
									/>
								</Grid>

								{/* Read only */}
								<Grid item xs={12}>
									<CustomCheckbox
										label="Read only"
										id="WriteProtected"
										name="WriteProtected"
										translationString="Procedures.EditProcedure.Readonly"
										type="checkbox"
										fullWidth
										sx={fieldSx}
									/>
								</Grid>
							</Grid>

							{/* Footer: Delete (left) • Close + Save (right) */}
							<DialogActions
								sx={{
									pt: 2,
									mt: 0.5,
									px: 0,
									gap: 1,
									alignItems: "center",
								}}
							>
								<Button
									variant="outlined"
									color="error"
									startIcon={<DeleteIcon />}
									onClick={() => handleDelete(values)}
									sx={buttonSx}
								>
									<Translate String="SATOSLive.Settings.Delete.procedure">
										Delete procedure
									</Translate>
								</Button>

								<Box sx={{ flex: 1 }} />

								<Button
									variant="outlined"
									color="warning"
									startIcon={<CloseIcon />}
									onClick={handleClose}
									sx={buttonSx}
								>
									<Translate String="Procedure.Button.Close">Close</Translate>
								</Button>

								<Button
									variant="contained"
									color="success"
									startIcon={<SaveIcon />}
									type="submit"
									sx={buttonSx}
									disabled={!isValid || !dirty}
								>
									<Translate String="Procedure.Button.Save">Save</Translate>
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};
